import {
  IWorkflowFormTemplateRevisionActions,
  IWorkflowFormFieldOptionUploadPayload,
  WorkflowFormTemplateContext,
  IWorkflowFormTemplateAttachmentDetailPayload,
  IWorkflowFormTemplateAttachmentCreatePayload,
  IWorkflowFormTemplateRevisionDetailPayload,
  IWorkflowFormTemplateRevisionDetailUpdatePayload,
  IWorkflowFormTemplateRevisionListPayload
} from './actions.types';
import {
  IWorkflowFormFieldData,
  IWorkflowFormFieldOptionTodoData,
  IWorkflowFormFieldOptionTodoTemplateData,
  IWorkflowFormTemplateRevisionData,
  IWorkflowFormFieldOptionData,
  IWorkflowFormTemplateData,
  WorkflowFormTemplateStateEnum,
  IWorkflowStateData,
  IUserData
} from '@/models';
import {
  IFetchWorkflowFormFieldParams,
  IFetchWorkflowFormTemplatesParams,
  IWorkflowFormFieldOptionTodosPayload,
  IUpdateWorkflowFormTemplateRevisionStatePayload,
  IFetchWorkflowFormTemplateRevisionsParams
} from '@/services';

export const actions: IWorkflowFormTemplateRevisionActions = {
  fetchWorkflowFormTemplatesWithPublishedRevisions(
    context: WorkflowFormTemplateContext
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .fetchWorkflowFormTemplates({
        formtemplaterevision__state: WorkflowFormTemplateStateEnum.Published
      })
      .then((workflowFormTemplates: IWorkflowFormTemplateData[]) => {
        context.commit(
          'SET_WORKFLOW_FORM_TEMPLATES_WITH_PUBLISHED_REVISIONS',
          workflowFormTemplates
        );
      });
  },

  updateWorkflowFormTemplateWithPublishedRevisions(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormTemplateData
  ): void {
    return context.commit(
      'UPSERT_WORKFLOW_FORM_TEMPLATE_WITH_PUBLISHED_REVISIONS',
      payload
    );
  },

  fetchWorkflowFormTemplates(
    context: WorkflowFormTemplateContext,
    params?: IFetchWorkflowFormTemplatesParams
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .fetchWorkflowFormTemplates(params)
      .then((workflowFormTemplates: IWorkflowFormTemplateData[]) => {
        context.commit('SET_WORKFLOW_FORM_TEMPLATES', workflowFormTemplates);
      });
  },

  fetchWorkflowFormTemplate(
    context: WorkflowFormTemplateContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .fetchWorkflowFormTemplate(payload)
      .then((workflowFormTemplate: IWorkflowFormTemplateData) => {
        context.commit('UPSERT_WORKFLOW_FORM_TEMPLATE', workflowFormTemplate);
      });
  },

  createWorkflowFormTemplate(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormTemplateData
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .createWorkflowFormTemplate(payload)
      .then((workflowFormTemplate: IWorkflowFormTemplateData) => {
        context.commit(
          'SET_LAST_CREATED_WORKFLOW_FORM_TEMPLATE_ID',
          workflowFormTemplate.id
        );
        context.commit('UPSERT_WORKFLOW_FORM_TEMPLATE', workflowFormTemplate);
      });
  },

  updateWorkflowFormTemplate(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormTemplateData
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .updateWorkflowFormTemplate(payload)
      .then((workflowFormTemplate: IWorkflowFormTemplateData) => {
        context.commit('UPSERT_WORKFLOW_FORM_TEMPLATE', workflowFormTemplate);
      });
  },

  deleteWorkflowFormTemplate(
    context: WorkflowFormTemplateContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .deleteWorkflowFormTemplate(payload)
      .then(() => {
        context.commit('REMOVE_WORKFLOW_FORM_TEMPLATE', payload);
      });
  },

  fetchWorkflowFormTemplatesRevisions(
    context: WorkflowFormTemplateContext,
    params?: IFetchWorkflowFormTemplateRevisionsParams
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .fetchWorkflowFormTemplatesRevisions(params)
      .then(
        (
          workflowFormTemplateRevisions: IWorkflowFormTemplateRevisionData[]
        ) => {
          context.commit(
            'SET_WORKFLOW_FORM_TEMPLATE_REVISIONS',
            workflowFormTemplateRevisions
          );
        }
      );
  },

  fetchWorkflowFormTemplateRevisions(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormTemplateRevisionListPayload
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .fetchWorkflowFormTemplateRevisions(payload.templateId, payload.params)
      .then(
        (
          workflowFormTemplateRevisions: IWorkflowFormTemplateRevisionData[]
        ) => {
          context.commit(
            'SET_WORKFLOW_FORM_TEMPLATE_REVISIONS',
            workflowFormTemplateRevisions
          );
        }
      );
  },

  fetchWorkflowFormTemplateRevision(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormTemplateRevisionDetailPayload
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .fetchWorkflowFormTemplateRevision(payload.templateId, payload.revisionId)
      .then(
        (workflowFormTemplateRevision: IWorkflowFormTemplateRevisionData) => {
          const index = context.state.workflowFormTemplateRevisions.findIndex(
            (v: IWorkflowFormTemplateRevisionData) =>
              v.id === workflowFormTemplateRevision.id
          );
          if (index === -1) {
            context.commit(
              'ADD_WORKFLOW_FORM_TEMPLATE_REVISION',
              workflowFormTemplateRevision
            );
          } else {
            context.commit(
              'UPDATE_WORKFLOW_FORM_TEMPLATE_REVISION',
              workflowFormTemplateRevision
            );
          }
        }
      );
  },

  createWorkflowFormTemplateRevision(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormTemplateRevisionDetailUpdatePayload
  ): Promise<IWorkflowFormTemplateRevisionData> {
    return this.$services.workflowFormTemplate
      .createWorkflowFormTemplateRevision(payload.templateId, payload.revision)
      .then(
        (workflowFormTemplateRevision: IWorkflowFormTemplateRevisionData) => {
          context.commit(
            'ADD_WORKFLOW_FORM_TEMPLATE_REVISION',
            workflowFormTemplateRevision
          );
          return workflowFormTemplateRevision;
        }
      );
  },

  updateWorkflowFormTemplateRevision(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormTemplateRevisionDetailUpdatePayload
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .updateWorkflowFormTemplateRevision(payload.templateId, payload.revision)
      .then((workflowFormTemplateRevision: IWorkflowFormTemplateData) => {
        const index = context.state.workflowFormTemplateRevisions.findIndex(
          (v: IWorkflowFormTemplateRevisionData) =>
            v.id === workflowFormTemplateRevision.id
        );
        if (index === -1) {
          context.commit(
            'ADD_WORKFLOW_FORM_TEMPLATE_REVISION',
            workflowFormTemplateRevision
          );
        } else {
          context.commit(
            'UPDATE_WORKFLOW_FORM_TEMPLATE_REVISION',
            workflowFormTemplateRevision
          );
        }
      });
  },

  deleteWorkflowFormTemplateRevision(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormTemplateRevisionDetailPayload
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .deleteWorkflowFormTemplateRevision(
        payload.templateId,
        payload.revisionId
      )
      .then(() => {
        context.commit(
          'REMOVE_WORKFLOW_FORM_TEMPLATE_REVISION',
          payload.revisionId
        );
      });
  },

  addWorkflowFormTemplateAttachment(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormTemplateAttachmentCreatePayload
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .addWorkflowFormTemplateAttachment(payload.templateId, payload.attachment)
      .then(workflowFormTemplate => {
        context.commit('UPSERT_WORKFLOW_FORM_TEMPLATE', workflowFormTemplate);

        const workflowFormTemplateRevision = context.state.workflowFormTemplateRevisions.find(
          revision => revision.template?.id === workflowFormTemplate.id
        );
        if (workflowFormTemplateRevision) {
          context.commit('UPDATE_WORKFLOW_FORM_TEMPLATE_REVISION', {
            ...workflowFormTemplateRevision,
            template: workflowFormTemplate
          });
        }
      });
  },

  removeWorkflowFormTemplateAttachment(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormTemplateAttachmentDetailPayload
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .removeWorkflowFormTemplateAttachment(
        payload.templateId,
        payload.attachmentId
      )
      .then(() => {
        const workflowFormTemplate = context.state.workflowFormTemplates.find(
          workflowFormTemplate => workflowFormTemplate.id === payload.templateId
        );

        if (workflowFormTemplate) {
          const attachmentIndex = workflowFormTemplate.attachments!.findIndex(
            attachment => attachment.id === payload.attachmentId
          );

          workflowFormTemplate.attachments!.splice(attachmentIndex, 1);

          context.commit('UPSERT_WORKFLOW_FORM_TEMPLATE', workflowFormTemplate);

          const workflowFormTemplateRevision = context.state.workflowFormTemplateRevisions.find(
            revision => revision.template?.id === workflowFormTemplate.id
          );
          if (workflowFormTemplateRevision) {
            context.commit('UPDATE_WORKFLOW_FORM_TEMPLATE_REVISION', {
              ...workflowFormTemplateRevision,
              template: workflowFormTemplate
            });
          }
        }
      });
  },

  createNewWorkflowTemplateRevision(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormTemplateRevisionDetailPayload
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .createNewWorkflowTemplateRevision(payload.templateId, payload.revisionId)
      .then((workflowFormTemplateRevision: IWorkflowFormTemplateData) => {
        context.commit(
          'ADD_WORKFLOW_FORM_TEMPLATE_REVISION',
          workflowFormTemplateRevision
        );
        context.commit(
          'SET_LAST_CREATED_WORKFLOW_FORM_TEMPLATE_REVISION_ID',
          workflowFormTemplateRevision.id
        );
      });
  },

  updateWorkflowFormTemplateRevisionState(
    context: WorkflowFormTemplateContext,
    payload: IUpdateWorkflowFormTemplateRevisionStatePayload
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .updateWorkflowFormTemplateRevisionState(payload)
      .then(
        (workflowFormTemplateRevision: IWorkflowFormTemplateRevisionData) => {
          const index = context.state.workflowFormTemplateRevisions.findIndex(
            (v: IWorkflowFormTemplateRevisionData) =>
              v.id === workflowFormTemplateRevision.id
          );
          if (index === -1) {
            context.commit(
              'ADD_WORKFLOW_FORM_TEMPLATE_REVISION',
              workflowFormTemplateRevision
            );
          } else {
            context.commit(
              'UPDATE_WORKFLOW_FORM_TEMPLATE_REVISION',
              workflowFormTemplateRevision
            );
          }

          context.commit(
            'UPSERT_WORKFLOW_FORM_TEMPLATE',
            workflowFormTemplateRevision.template
          );
        }
      );
  },

  fetchTemplateWorkflowStates(
    context: WorkflowFormTemplateContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .fetchTemplateWorkflowStates(payload)
      .then((workflowStates: IWorkflowStateData[]) => {
        context.commit('SET_TEMPLATE_WORKFLOW_STATES', workflowStates);
      });
  },

  fetchWorkflowTemplateUsers(
    context: WorkflowFormTemplateContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .fetchWorkflowTemplateUsers(payload)
      .then((users: IUserData[]) => {
        context.commit('SET_WORKFLOW_TEMPLATE_USERS', users);
      });
  },

  //fields
  fetchWorkflowFormFields(
    context: WorkflowFormTemplateContext,
    params: IFetchWorkflowFormFieldParams
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .fetchWorkflowFormFields(params)
      .then((formFields: IWorkflowFormFieldData[]) => {
        context.commit('SET_WORKFLOW_FORM_FIELDS', formFields);
      });
  },

  fetchWorkflowFormField(
    context: WorkflowFormTemplateContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .fetchWorkflowFormField(payload)
      .then((formField: IWorkflowFormFieldData) => {
        const index = context.state.formFields.findIndex(
          (v: IWorkflowFormFieldData) => v.id === formField.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_FIELD', formField);
        } else {
          context.commit('UPDATE_WORKFLOW_FORM_FIELD', formField);
        }
      });
  },

  createWorkflowFormField(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormFieldData
  ): Promise<IWorkflowFormFieldData> {
    return this.$services.workflowFormTemplate
      .createWorkflowFormField(payload)
      .then((formField: IWorkflowFormFieldData) => {
        context.commit('ADD_WORKFLOW_FORM_FIELD', formField);
        return formField;
      });
  },

  updateWorkflowFormField(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormFieldData
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .updateWorkflowFormField(payload)
      .then((formField: IWorkflowFormFieldData) => {
        const index = context.state.formFields.findIndex(
          (v: IWorkflowFormFieldData) => v.id === formField.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_FIELD', formField);
        } else {
          context.commit('UPDATE_WORKFLOW_FORM_FIELD', formField);
        }
      });
  },

  uploadWorkflowFormFieldOptions(
    _context: WorkflowFormTemplateContext,
    payload: IWorkflowFormFieldOptionUploadPayload
  ): Promise<IWorkflowFormFieldOptionData[]> {
    return this.$services.workflowFormTemplate.uploadWorkflowFormFieldOptions(
      payload.fieldId,
      payload.file
    );
  },

  deleteWorkflowFormField(
    context: WorkflowFormTemplateContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .deleteWorkflowFormField(payload)
      .then(() => {
        context.commit('REMOVE_WORKFLOW_FORM_FIELD', payload);
      });
  },

  //todos
  fetchWorkflowFormFieldOptionTodos(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormFieldOptionTodosPayload
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .fetchWorkflowFormFieldOptionTodos(payload.params!)
      .then((formFields: IWorkflowFormFieldOptionTodoData[]) => {
        context.commit('SET_WORKFLOW_FORM_FIELD_OPTION_TODOS', formFields);
      });
  },

  createWorkflowFormFieldOptionTodo(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormFieldOptionTodosPayload
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .createWorkflowFormFieldOptionTodo(payload.params!, payload.data!)
      .then((formField: IWorkflowFormFieldOptionTodoData) =>
        context.commit('ADD_WORKFLOW_FORM_FIELD_OPTION_TODO', formField)
      );
  },

  updateWorkflowFormFieldOptionTodo(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormFieldOptionTodosPayload
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .updateWorkflowFormFieldOptionTodo(payload.params!, payload.data!)
      .then((formField: IWorkflowFormFieldOptionTodoData) => {
        const index = context.state.formFieldOptionTodos.findIndex(
          (v: IWorkflowFormFieldOptionTodoData) => v.id === formField.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_FIELD_OPTION_TODO', formField);
        } else {
          context.commit('UPDATE_WORKFLOW_FORM_FIELD_OPTION_TODO', formField);
        }
      });
  },

  deleteWorkflowFormFieldOptionTodo(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormFieldOptionTodosPayload
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .deleteWorkflowFormFieldOptionTodo(payload.params!, payload.id!)
      .then(() => {
        context.commit('REMOVE_WORKFLOW_FORM_FIELD_OPTION_TODO', payload);
      });
  },

  //todo template
  fetchWorkflowFormTodoTemplates(
    context: WorkflowFormTemplateContext
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .fetchWorkflowFormTodoTemplates()
      .then((formTodoTemplates: IWorkflowFormFieldOptionTodoTemplateData[]) => {
        context.commit('SET_WORKFLOW_FORM_TODO_TEMPLATES', formTodoTemplates);
      });
  },

  fetchWorkflowFormTodoTemplate(
    context: WorkflowFormTemplateContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .fetchWorkflowFormTodoTemplate(payload)
      .then((formTodoTemplate: IWorkflowFormFieldOptionTodoTemplateData) => {
        const index = context.state.formTodoTempates.findIndex(
          (v: IWorkflowFormFieldOptionTodoTemplateData) =>
            v.id === formTodoTemplate.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_TODO_TEMPLATE', formTodoTemplate);
        } else {
          context.commit(
            'UPDATE_WORKFLOW_FORM_TODO_TEMPLATE',
            formTodoTemplate
          );
        }
      });
  },

  createWorkflowFormTodoTemplate(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormFieldOptionTodoTemplateData
  ): Promise<IWorkflowFormFieldOptionTodoTemplateData> {
    return this.$services.workflowFormTemplate
      .createWorkflowFormTodoTemplate(payload)
      .then((formTodoTemplate: IWorkflowFormFieldOptionTodoTemplateData) => {
        context.commit('ADD_WORKFLOW_FORM_TODO_TEMPLATE', formTodoTemplate);
        return formTodoTemplate;
      });
  },

  updateWorkflowFormTodoTemplate(
    context: WorkflowFormTemplateContext,
    payload: IWorkflowFormFieldOptionTodoTemplateData
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .updateWorkflowFormTodoTemplate(payload)
      .then((formTodoTemplate: IWorkflowFormFieldOptionTodoTemplateData) => {
        const index = context.state.formTodoTempates.findIndex(
          (v: IWorkflowFormFieldOptionTodoTemplateData) =>
            v.id === formTodoTemplate.id
        );
        if (index === -1) {
          context.commit('ADD_WORKFLOW_FORM_TODO_TEMPLATE', formTodoTemplate);
        } else {
          context.commit(
            'UPDATE_WORKFLOW_FORM_TODO_TEMPLATE',
            formTodoTemplate
          );
        }
      });
  },

  deleteWorkflowFormTodoTemplate(
    context: WorkflowFormTemplateContext,
    payload: number
  ): Promise<void> {
    return this.$services.workflowFormTemplate
      .deleteWorkflowFormTodoTemplate(payload)
      .then(() => {
        context.commit('REMOVE_WORKFLOW_FORM_TODO_TEMPLATE', payload);
      });
  }
};
